/**
 * Client-side DOCX export utility using the docx package
 * Converts TipTap editor JSON to DOCX with true Word track changes and comments
 *
 * Enhanced to support:
 * - Text alignment (left, center, right, justify)
 * - Underline formatting
 * - Multi-level hierarchical numbering (1, 1.1, 1.2, 1.2.1, etc.)
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  InsertedTextRun,
  DeletedTextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  ImageRun,
  CommentRangeStart,
  CommentRangeEnd,
  CommentReference,
  LevelFormat,
  convertInchesToTwip,
  PageBreak,
  Header,
  Footer,
  PageNumber,
  FootnoteReferenceRun,
  Tab,
  TabStopType,
  ExternalHyperlink,
  Math as DocxMath,
} from "docx";
import { toRoman } from "../extensions/FootnoteExtension";
import { latexToDocxMath } from "./latexToDocxMath";
import { primaryFontName } from "./fontMap";
import {
  PageSetup,
  DEFAULT_PAGE_SETUP,
  pageSetupToDocxPage,
  pageDimensionsIn,
} from "./pageSetup";

/**
 * Legacy flat header/footer config (the exporter's input shape — rich
 * 3-zone configs are flattened to this before export). Center zones are
 * optional for back-compat with configs persisted before the in-place
 * editor existed.
 */
export interface HeaderFooterConfig {
  headerLeft: string;
  headerRight: string;
  footerLeft: string;
  footerRight: string;
  differentFirstPage: boolean;
  firstPageHeaderLeft: string;
  firstPageHeaderRight: string;
  firstPageFooterLeft: string;
  firstPageFooterRight: string;
  headerCenter?: string;
  footerCenter?: string;
  firstPageHeaderCenter?: string;
  firstPageFooterCenter?: string;
}

/**
 * Interface for TipTap editor JSON node
 */
interface TipTapNode {
  type: string;
  content?: TipTapNode[];
  text?: string;
  marks?: Array<{
    type: string;
    attrs?: Record<string, any>;
  }>;
  attrs?: Record<string, any>;
}

// Type for document children (can be Paragraph or Table)
type DocChild = Paragraph | Table;

/**
 * Decode the HTML entities the zone editors can emit (named + numeric).
 * `&amp;` is decoded LAST so `&amp;lt;` becomes `&lt;`, not `<`.
 */
function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(parseInt(d, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) =>
      String.fromCodePoint(parseInt(h, 16))
    )
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

// Comment data interface
export interface CommentData {
  id: string;
  content: string;
  author: string;
  createdAt: Date;
  replies?: CommentData[];
  resolvedAt?: Date;
}

export interface DocxExportOptions {
  author?: string;
  title?: string;
  comments?: CommentData[];
  headerFooter?: HeaderFooterConfig;
  /** Page size / orientation / margins. Defaults to the editor's Letter setup. */
  pageSetup?: PageSetup;
}

/**
 * Convert color to docx hex format (6 digits, no #).
 * Handles: #RRGGBB, RRGGBB, rgb(r,g,b), rgba(r,g,b,a) - from Word paste or CSS.
 * Returns undefined for invalid formats.
 */
function toHexColor(color: string | undefined): string | undefined {
  if (!color) return undefined;
  if (/^#?[0-9A-Fa-f]{6}$/.test(color)) {
    return color.replace(/^#/, "");
  }
  const rgbMatch = color.match(
    /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)$/
  );
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, "0");
    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, "0");
    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, "0");
    return `${r}${g}${b}`.toUpperCase();
  }
  return undefined;
}

/**
 * Convert TipTap editor JSON to DOCX with true Word track changes and comments
 * @param json - TipTap editor JSON content
 * @param options - Export options
 * @returns Blob containing the DOCX file
 */
export async function exportToDocxWithTrackChanges(
  json: TipTapNode,
  options: DocxExportOptions = {}
): Promise<Blob> {
  const author = options.author || "Document Author";
  const revisionDate = new Date();

  // Pre-fetch bytes for images whose src is an http(s) URL (imported/GCS/proxy
  // images) BEFORE the synchronous processNode walk. Otherwise those images
  // only ever exported as an italic "[Image: https://…]" placeholder, so an
  // import→export round trip destroyed every figure. processNode reads embedded
  // bytes from this map synchronously.
  const imageBytesBySrc = new Map<string, Uint8Array>();
  {
    const urls = new Set<string>();
    const collect = (n: TipTapNode | undefined) => {
      if (!n) return;
      if (n.type === "image") {
        const s = n.attrs?.src;
        if (typeof s === "string" && /^https?:\/\//i.test(s)) urls.add(s);
      }
      (n.content || []).forEach(collect);
    };
    collect(json);
    await Promise.all(
      Array.from(urls).map(async url => {
        try {
          // Backend image-proxy URLs are auth'd + cross-origin; send credentials.
          const isBackend = /\/api\/image-proxy\//.test(url);
          const resp = await fetch(url, {
            credentials: isBackend ? "include" : "omit",
          });
          if (!resp.ok) return;
          const buf = await resp.arrayBuffer();
          imageBytesBySrc.set(url, new Uint8Array(buf));
        } catch (e) {
          console.warn("[docxExport] Failed to fetch image for export:", url, e);
        }
      })
    );
  }

  // Map comment IDs to numeric IDs for Word (Word uses numeric IDs)
  // Skip resolved comments — they are not exported to Word
  const commentIdMap = new Map<string, number>();
  const commentsData = (options.comments || []).filter(c => !c.resolvedAt);
  commentsData.forEach((comment, index) => {
    commentIdMap.set(comment.id, index);
  });

  // Default spacing for paragraphs (in twips: 1/20 of a point)
  // 120 twips after = ~6pt space after paragraph (comfortable reading)
  // line = 276 = 1.15x line spacing (Word default)
  const defaultSpacing = {
    after: 120,
    line: 276,
    lineRule: "auto" as const,
  };

  // Heading spacing (more space before/after)
  const headingSpacing = {
    before: 240,
    after: 120,
    line: 276,
    lineRule: "auto" as const,
  };

  // Each independent ordered list gets its own numbering INSTANCE so it
  // restarts at 1. docx defaults instance to 0, so without this every ordered
  // list shared one concrete numbering and the second list continued the first
  // (3,4,5 instead of 1,2,3) — very common in office-action responses.
  let orderedListInstanceCounter = 0;

  function processNode(node: TipTapNode): DocChild[] {
    const result: DocChild[] = [];

    if (node.type === "doc" && node.content) {
      for (const child of node.content) {
        result.push(...processNode(child));
      }
      return result;
    }

    if (node.type === "footnotesSection") {
      const kind = node.attrs?.kind === "endnote" ? "endnote" : "footnote";
      if (kind === "footnote") {
        // Real Word footnotes — content is emitted via Document({ footnotes })
        // and FootnoteReferenceRun, so nothing goes into the body here.
        return result;
      }
      // Endnotes: the docx lib has no endnote support, so the section is
      // exported as literal content with its numbering baked in.
      result.push(
        new Paragraph({
          children: [new TextRun({ text: "Endnotes", bold: true })],
          spacing: headingSpacing,
        })
      );
      for (const item of node.content || []) {
        if (item.type !== "footnoteItem") continue;
        const num = endnoteNumbers.get(item.attrs?.footnoteId || "");
        const label = num ? toRoman(num) : "?";
        (item.content || []).forEach((child, idx) => {
          if (child.type === "paragraph") {
            const children = processInlineContent(child.content || []);
            const runs =
              idx === 0
                ? [new TextRun({ text: `${label}. ` }), ...children]
                : children;
            result.push(
              new Paragraph({ children: runs, spacing: defaultSpacing })
            );
          } else {
            result.push(...processNode(child));
          }
        });
      }
      return result;
    }

    // Helper to convert TipTap text alignment to docx AlignmentType
    const getAlignment = (textAlign: string | undefined) => {
      switch (textAlign) {
        case "left":
          return AlignmentType.LEFT;
        case "center":
          return AlignmentType.CENTER;
        case "right":
          return AlignmentType.RIGHT;
        case "justify":
          return AlignmentType.JUSTIFIED;
        default:
          return undefined;
      }
    };

    if (node.type === "paragraph") {
      const children = processInlineContent(node.content || []);
      const alignment = getAlignment(node.attrs?.textAlign);

      // Build custom spacing if provided in attrs (from imported DOCX)
      const spacing: {
        before?: number;
        after: number;
        line: number;
        lineRule: "auto";
      } = {
        ...defaultSpacing,
      };
      if (node.attrs?.lineHeight) {
        // Convert line height multiplier to Word's format (240 = 1.0)
        spacing.line = Math.round(parseFloat(node.attrs.lineHeight) * 240);
      }
      if (node.attrs?.marginTop) {
        // Convert pt to twips (1pt = 20 twips)
        spacing.before = Math.round(parseFloat(node.attrs.marginTop) * 20);
      }
      if (node.attrs?.marginBottom) {
        spacing.after = Math.round(parseFloat(node.attrs.marginBottom) * 20);
      }

      // Build indentation if provided
      const indent: {
        left?: number;
        right?: number;
        firstLine?: number;
        hanging?: number;
      } = {};
      if (node.attrs?.marginLeft) {
        indent.left = Math.round(parseFloat(node.attrs.marginLeft) * 20);
      }
      if (node.attrs?.marginRight) {
        indent.right = Math.round(parseFloat(node.attrs.marginRight) * 20);
      }
      if (node.attrs?.textIndent) {
        const textIndentPt = parseFloat(node.attrs.textIndent);
        if (textIndentPt > 0) {
          indent.firstLine = Math.round(textIndentPt * 20);
        } else if (textIndentPt < 0) {
          indent.hanging = Math.round(Math.abs(textIndentPt) * 20);
        }
      }

      result.push(
        new Paragraph({
          children,
          spacing,
          alignment,
          indent: Object.keys(indent).length > 0 ? indent : undefined,
        })
      );
      return result;
    }

    if (node.type === "heading") {
      const level = node.attrs?.level || 1;
      const headingLevel =
        [
          HeadingLevel.HEADING_1,
          HeadingLevel.HEADING_2,
          HeadingLevel.HEADING_3,
          HeadingLevel.HEADING_4,
          HeadingLevel.HEADING_5,
          HeadingLevel.HEADING_6,
        ][level - 1] || HeadingLevel.HEADING_1;

      const children = processInlineContent(node.content || []);
      const alignment = getAlignment(node.attrs?.textAlign);
      result.push(
        new Paragraph({
          heading: headingLevel,
          children,
          spacing: headingSpacing,
          alignment,
        })
      );
      return result;
    }

    if (node.type === "bulletList" || node.type === "orderedList") {
      const isOrdered = node.type === "orderedList";
      // Smaller spacing for list items
      const listSpacing = { after: 60, line: 276, lineRule: "auto" as const };

      // Process list items recursively to handle nested lists
      const processListItems = (
        items: TipTapNode[],
        level: number,
        parentIsOrdered: boolean,
        instance: number
      ): void => {
        for (const listItem of items) {
          if (listItem.type === "listItem" && listItem.content) {
            for (const itemContent of listItem.content) {
              if (itemContent.type === "paragraph") {
                const children = processInlineContent(
                  itemContent.content || []
                );
                const alignment = getAlignment(itemContent.attrs?.textAlign);
                result.push(
                  new Paragraph({
                    children,
                    bullet: parentIsOrdered ? undefined : { level },
                    numbering: parentIsOrdered
                      ? { reference: "outline-numbering", level, instance }
                      : undefined,
                    spacing: listSpacing,
                    alignment,
                  })
                );
              } else if (
                itemContent.type === "bulletList" ||
                itemContent.type === "orderedList"
              ) {
                // Nested list - recurse with incremented level. Entering an
                // ordered list from a NON-ordered parent starts a new numbering
                // root (fresh instance); an ordered list nested in an ordered
                // list continues the same numbering (multilevel), so keep the
                // instance.
                const nestedIsOrdered = itemContent.type === "orderedList";
                const nestedInstance =
                  nestedIsOrdered && !parentIsOrdered
                    ? orderedListInstanceCounter++
                    : instance;
                processListItems(
                  itemContent.content || [],
                  level + 1,
                  nestedIsOrdered,
                  nestedInstance
                );
              }
            }
          }
        }
      };

      if (node.content) {
        const rootInstance = isOrdered ? orderedListInstanceCounter++ : 0;
        processListItems(node.content, 0, isOrdered, rootInstance);
      }
      return result;
    }

    if (node.type === "blockquote" && node.content) {
      for (const child of node.content) {
        const items = processNode(child);
        for (const item of items) {
          if (item instanceof Paragraph) {
            // Re-wrap the paragraph's runs with a blockquote indent. The runs
            // are root[1..] (root[0] is ParagraphProperties); the old
            // root[1].children was always undefined (root[1] is a TextRun, which
            // has no .children), so all blockquote text was silently dropped.
            result.push(
              new Paragraph({
                children: ((item as any).root?.slice(1) as any[]) || [],
                indent: { left: 720 }, // 0.5 inch indent
                spacing: defaultSpacing,
              })
            );
          } else {
            result.push(item);
          }
        }
      }
      return result;
    }

    if (node.type === "codeBlock") {
      const text = getTextContent(node);
      result.push(
        new Paragraph({
          children: [
            new TextRun({
              text,
              font: "Courier New",
              size: 20,
            }),
          ],
        })
      );
      return result;
    }

    if (node.type === "tableOfContents") {
      // Expand the auto-TOC node into a static heading list (the node itself is
      // an empty marker; its live list lives only in the editor node view).
      result.push(
        new Paragraph({
          children: [new TextRun({ text: "Table of Contents", bold: true, size: 28 })],
          spacing: { after: 120 },
        })
      );
      const tocHeadings: { level: number; text: string }[] = [];
      const walkHeadings = (n: TipTapNode | undefined) => {
        if (!n) return;
        if (n.type === "heading") {
          tocHeadings.push({
            level: n.attrs?.level || 1,
            text: getTextContent(n).trim(),
          });
        }
        (n.content || []).forEach(walkHeadings);
      };
      walkHeadings(json);
      const minLvl = tocHeadings.length
        ? Math.min(...tocHeadings.map(h => h.level))
        : 1;
      for (const h of tocHeadings) {
        result.push(
          new Paragraph({
            children: [new TextRun({ text: h.text || "(untitled)" })],
            indent: { left: (h.level - minLvl) * 360 },
            spacing: { after: 40 },
          })
        );
      }
      return result;
    }

    if (node.type === "horizontalRule") {
      // Check if this is a page break (has data-type="page-break" or class contains "page-break")
      const isPageBreak =
        node.attrs?.["data-type"] === "page-break" ||
        node.attrs?.class?.includes("page-break");

      if (isPageBreak) {
        // Create a Word page break
        result.push(
          new Paragraph({
            children: [new PageBreak()],
          })
        );
      } else {
        // Regular horizontal rule - create a visible line
        result.push(
          new Paragraph({
            border: {
              bottom: { style: "single" as any, size: 6, color: "000000" },
            },
          })
        );
      }
      return result;
    }

    // Handle page break node (from PageBreak extension)
    if (node.type === "pageBreak") {
      // Create a Word page break
      result.push(
        new Paragraph({
          children: [new PageBreak()],
        })
      );
      return result;
    }

    // Handle math nodes — real OOXML Math when the LaTeX is in the
    // supported subset, italic $latex$ text otherwise
    if (node.type === "inlineMath" || node.type === "blockMath") {
      const latex = node.attrs?.latex || "";
      const math = latexToDocxMath(latex);
      result.push(
        new Paragraph({
          children: math
            ? [math]
            : [
                new TextRun({
                  text:
                    node.type === "blockMath" ? `$$${latex}$$` : `$${latex}$`,
                  italics: true,
                }),
              ],
          alignment:
            node.type === "blockMath" ? AlignmentType.CENTER : undefined,
        })
      );
      return result;
    }

    // Handle image nodes
    if (node.type === "image") {
      const src = node.attrs?.src || "";
      const alt = node.attrs?.alt || "";

      if (src) {
        // Check if it's a base64 data URL
        if (src.startsWith("data:image/")) {
          try {
            const matches = src.match(/^data:image\/(\w+);base64,(.+)$/);
            if (matches) {
              const base64Data = matches[2];
              // Convert base64 to Uint8Array for browser
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }

              result.push(
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: bytes,
                      transformation: {
                        width: 400,
                        height: 300,
                      },
                    }),
                  ],
                })
              );
              return result;
            }
          } catch (error) {
            console.error("Error processing base64 image:", error);
          }
        }

        // http(s) image whose bytes we pre-fetched → embed as a real picture.
        const prefetched = imageBytesBySrc.get(src);
        if (prefetched) {
          const w = Number(node.attrs?.width);
          const h = Number(node.attrs?.height);
          result.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: prefetched,
                  transformation: {
                    width: Number.isFinite(w) && w > 0 ? w : 400,
                    height: Number.isFinite(h) && h > 0 ? h : 300,
                  },
                }),
              ],
            })
          );
          return result;
        }

        // Fallback: add placeholder text
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `[Image: ${alt || src}]`,
                italics: true,
              }),
            ],
          })
        );
      }
      return result;
    }

    // Handle multi-column layout (Wave 3d). Word models newspaper columns at
    // the section level, which would force a multi-section export and break
    // header/footer inheritance. Instead we emit the block as a single-row,
    // borderless N-column table — the side-by-side layout survives in Word and
    // re-imports cleanly as a table. Content is distributed across the cells to
    // approximate the on-screen balanced column flow.
    if (node.type === "columns" && node.content) {
      const colCount = Math.max(
        2,
        Math.min(3, Number(node.attrs?.cols) || 2)
      );
      const blocks = node.content;
      const perCol = Math.max(1, Math.ceil(blocks.length / colCount));
      const noBorder = { style: "none" as const, size: 0, color: "FFFFFF" };
      const allNoBorders = {
        top: noBorder,
        bottom: noBorder,
        left: noBorder,
        right: noBorder,
        insideHorizontal: noBorder,
        insideVertical: noBorder,
      };
      const cells: TableCell[] = [];
      for (let c = 0; c < colCount; c++) {
        const slice = blocks.slice(c * perCol, (c + 1) * perCol);
        const cellChildren: DocChild[] = [];
        for (const b of slice) {
          for (const item of processNode(b)) {
            cellChildren.push(item);
          }
        }
        if (cellChildren.length === 0) cellChildren.push(new Paragraph({}));
        cells.push(
          new TableCell({
            width: {
              size: Math.floor(100 / colCount),
              type: WidthType.PERCENTAGE,
            },
            margins: { left: 120, right: 120 },
            borders: {
              top: noBorder,
              bottom: noBorder,
              left: noBorder,
              right: noBorder,
            },
            children: cellChildren,
          })
        );
      }
      return [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: allNoBorders,
          rows: [new TableRow({ children: cells })],
        }),
      ];
    }

    // Handle table
    if (node.type === "table" && node.content) {
      const rows: TableRow[] = [];
      for (const rowNode of node.content) {
        if (rowNode.type === "tableRow" && rowNode.content) {
          const cells: TableCell[] = [];
          for (const cellNode of rowNode.content) {
            const isHeader = cellNode.type === "tableHeader";
            const cellContent = cellNode.content || [];
            const cellParagraphs: Paragraph[] = [];

            for (const cellChild of cellContent) {
              const childItems = processNode(cellChild);
              for (const item of childItems) {
                if (item instanceof Paragraph) {
                  cellParagraphs.push(item);
                }
              }
            }

            // Build shading from cell attributes or default for headers
            // Note: TipTap stores cell bg as "background" (from TableCellStyleExtension)
            let shading: { fill?: string; color?: string } | undefined;
            const cellBg =
              cellNode.attrs?.background || cellNode.attrs?.backgroundColor;
            if (cellBg) {
              const bgColor = toHexColor(cellBg);
              if (bgColor) shading = { fill: bgColor };
            } else if (isHeader) {
              shading = { fill: "DDDDDD" };
            }

            // Build borders from cell attributes if provided
            const parseBorderStyle = (borderStr: string | undefined) => {
              if (!borderStr) return undefined;
              // Parse "1pt solid #000000" or "1pt solid rgb(0, 0, 0)" format
              const match = borderStr.match(
                /^(\d+(?:\.\d+)?)\s*pt\s+(\w+)\s+(.+)$/
              );
              if (match) {
                const hexColor = toHexColor(match[3].trim());
                if (!hexColor) return undefined;
                const size = Math.round(parseFloat(match[1]) * 8); // Convert pt to eighths of pt
                const style =
                  match[2] === "double"
                    ? "double"
                    : match[2] === "dashed"
                      ? "dashed"
                      : match[2] === "dotted"
                        ? "dotted"
                        : "single";
                return { style, size, color: hexColor };
              }
              return undefined;
            };

            const borders: Record<string, any> = {};
            const topBorder = parseBorderStyle(cellNode.attrs?.borderTop);
            const bottomBorder = parseBorderStyle(cellNode.attrs?.borderBottom);
            const leftBorder = parseBorderStyle(cellNode.attrs?.borderLeft);
            const rightBorder = parseBorderStyle(cellNode.attrs?.borderRight);

            if (topBorder) borders.top = topBorder;
            if (bottomBorder) borders.bottom = bottomBorder;
            if (leftBorder) borders.left = leftBorder;
            if (rightBorder) borders.right = rightBorder;

            // Vertical alignment
            const verticalAlignMap: Record<string, any> = {
              top: "top",
              middle: "center",
              bottom: "bottom",
            };
            const verticalAlign = cellNode.attrs?.verticalAlign
              ? verticalAlignMap[cellNode.attrs.verticalAlign]
              : undefined;

            // Preserve colspan / rowspan from TipTap attrs
            const columnSpan = cellNode.attrs?.colspan as number | undefined;
            const rowSpan = cellNode.attrs?.rowspan as number | undefined;

            // Preserve original column width if available (stored as px array by the OOXML parser).
            // Fall back to equal distribution.
            const colwidthArr = cellNode.attrs?.colwidth as
              | number[]
              | undefined;
            let cellWidth: {
              size: number;
              type: typeof WidthType.DXA | typeof WidthType.PERCENTAGE;
            };
            if (colwidthArr && colwidthArr.length > 0) {
              // Convert px back to DXA (twips): px / 1.333 * 20 ≈ px * 15
              const totalPx = colwidthArr.reduce((a, b) => a + b, 0);
              cellWidth = {
                size: Math.round(totalPx * 15),
                type: WidthType.DXA,
              };
            } else {
              cellWidth = {
                size: 100 / (rowNode.content?.length || 1),
                type: WidthType.PERCENTAGE,
              };
            }

            cells.push(
              new TableCell({
                children:
                  cellParagraphs.length > 0
                    ? cellParagraphs
                    : [new Paragraph({})],
                shading,
                width: cellWidth,
                borders: Object.keys(borders).length > 0 ? borders : undefined,
                verticalAlign,
                columnSpan:
                  columnSpan && columnSpan > 1 ? columnSpan : undefined,
                rowSpan: rowSpan && rowSpan > 1 ? rowSpan : undefined,
              })
            );
          }
          if (cells.length > 0) {
            rows.push(new TableRow({ children: cells }));
          }
        }
      }

      if (rows.length > 0) {
        result.push(
          new Table({
            rows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          })
        );
        result.push(new Paragraph({}));
      }
      return result;
    }

    // Default: process children
    if (node.content) {
      for (const child of node.content) {
        result.push(...processNode(child));
      }
    }

    return result;
  }

  // Track which comments we've seen (to add to document level)
  const seenCommentIds = new Set<string>();

  // Pre-scan: count how many text nodes each commentId appears on.
  // This lets us emit CommentRangeStart only on the first occurrence
  // and CommentRangeEnd + CommentReference only on the last.
  const commentTotalOccurrences = new Map<string, number>();
  const commentSeenOccurrences = new Map<string, number>();

  function prescanComments(node: TipTapNode) {
    if (node.type === "text" && node.marks) {
      for (const mark of node.marks) {
        if (mark.type === "comment" && mark.attrs?.commentId) {
          const id = mark.attrs.commentId as string;
          commentTotalOccurrences.set(
            id,
            (commentTotalOccurrences.get(id) || 0) + 1
          );
        }
      }
    }
    if (node.content) {
      for (const child of node.content) {
        prescanComments(child);
      }
    }
  }
  prescanComments(json);

  // Pre-scan footnote/endnote refs: number them in document order (refs inside
  // the notes sections themselves don't count).
  const footnoteNumbers = new Map<string, number>();
  const endnoteNumbers = new Map<string, number>();
  function prescanFootnotes(node: TipTapNode, insideSection: boolean) {
    const nowInside = insideSection || node.type === "footnotesSection";
    if (!nowInside && node.type === "footnoteRef" && node.attrs?.footnoteId) {
      const id = node.attrs.footnoteId as string;
      const map =
        node.attrs.kind === "endnote" ? endnoteNumbers : footnoteNumbers;
      if (!map.has(id)) map.set(id, map.size + 1);
    }
    if (node.content) {
      for (const child of node.content) {
        prescanFootnotes(child, nowInside);
      }
    }
  }
  prescanFootnotes(json, false);

  function processInlineContent(
    content: TipTapNode[]
  ): (
    | TextRun
    | InsertedTextRun
    | DeletedTextRun
    | ImageRun
    | CommentRangeStart
    | CommentRangeEnd
    | CommentReference
    | ExternalHyperlink
    | DocxMath
  )[] {
    const runs: (
      | TextRun
      | InsertedTextRun
      | DeletedTextRun
      | ImageRun
      | CommentRangeStart
      | CommentRangeEnd
      | CommentReference
      | ExternalHyperlink
      | DocxMath
    )[] = [];

    for (const node of content) {
      if (node.type === "text") {
        const text = node.text || "";
        const marks = node.marks || [];

        // Check for track change marks
        const insertionMark = marks.find(m => m.type === "insertion");
        const deletionMark = marks.find(m => m.type === "deletion");

        // Check for comment marks - handle ALL comments on this text node
        const commentMarks = marks.filter(
          m => m.type === "comment" && m.attrs?.commentId
        );
        // Collect all valid comment IDs on this text node
        const activeCommentIds = commentMarks
          .map(m => m.attrs!.commentId as string)
          .filter(id => commentIdMap.has(id));

        // Determine text formatting
        const isBold = marks.some(m => m.type === "bold");
        const isItalic = marks.some(m => m.type === "italic");
        const isUnderline = marks.some(m => m.type === "underline");
        const isStrike = marks.some(m => m.type === "strike");
        const isCode = marks.some(m => m.type === "code");
        const isSubscript = marks.some(m => m.type === "subscript");
        const isSuperscript = marks.some(m => m.type === "superscript");

        // Get text color from textStyle mark
        const textStyleMark = marks.find(m => m.type === "textStyle");
        const textColor = textStyleMark?.attrs?.color;

        // Get font family and size
        const fontFamily = textStyleMark?.attrs?.fontFamily;
        const fontSize = textStyleMark?.attrs?.fontSize;

        // Get highlight/background color
        const highlightMark = marks.find(m => m.type === "highlight");
        const backgroundColor =
          highlightMark?.attrs?.color || textStyleMark?.attrs?.backgroundColor;

        // Link mark → ExternalHyperlink (Word-clickable). Text carrying
        // track-change marks keeps the tracked run instead (the docx lib
        // can't nest Inserted/DeletedTextRun inside a hyperlink).
        const linkMark = marks.find(m => m.type === "link");
        const linkHref = linkMark?.attrs?.href as string | undefined;

        // Convert font size (e.g., "14pt" -> 28 half-points)
        const formatFontSize = (
          size: string | undefined
        ): number | undefined => {
          if (!size) return undefined;
          const match = size.match(/^(\d+)/);
          if (match) {
            return parseInt(match[1], 10) * 2; // Convert pt to half-points
          }
          return undefined;
        };

        const baseOptions: any = {
          text,
          bold: isBold,
          italics: isItalic,
          underline: isUnderline ? {} : undefined,
          strike: isStrike,
          color: toHexColor(textColor),
          // De-stack before handing to the docx lib: fontFamily is a CSS stack
          // ("Calibri, Candara, sans-serif") but docx copies the string verbatim
          // into w:rFonts, so Word would show the literal stack as an unknown
          // font and substitute glyphs. Emit just the primary family name.
          font: fontFamily
            ? primaryFontName(fontFamily)
            : isCode
              ? "Courier New"
              : undefined,
          size: formatFontSize(fontSize),
          subScript: isSubscript,
          superScript: isSuperscript,
          shading: backgroundColor
            ? {
                fill: toHexColor(backgroundColor),
              }
            : undefined,
        };

        // Plain (untracked) run, wrapped in a clickable hyperlink when a
        // link mark is present
        const buildTextOrLinkRun = (): TextRun | ExternalHyperlink => {
          if (linkHref) {
            return new ExternalHyperlink({
              children: [new TextRun({ ...baseOptions, style: "Hyperlink" })],
              link: linkHref,
            });
          }
          return new TextRun(baseOptions);
        };

        // Get author/date from mark attributes if available, otherwise use defaults
        const insertionAuthor = insertionMark?.attrs?.author || author;
        const insertionDate =
          insertionMark?.attrs?.date || revisionDate.toISOString();
        const deletionAuthor = deletionMark?.attrs?.author || author;
        const deletionDate =
          deletionMark?.attrs?.date || revisionDate.toISOString();

        // Handle comments - emit start/end markers for ALL comment IDs on this text node
        if (activeCommentIds.length > 0) {
          // Emit CommentRangeStart for each comment that appears here for the first time
          for (const cId of activeCommentIds) {
            seenCommentIds.add(cId);
            const seenSoFar = (commentSeenOccurrences.get(cId) || 0) + 1;
            commentSeenOccurrences.set(cId, seenSoFar);
            if (seenSoFar === 1) {
              runs.push(new CommentRangeStart(commentIdMap.get(cId)!));
            }
          }

          // Add the text run
          if (insertionMark) {
            runs.push(
              new InsertedTextRun({
                ...baseOptions,
                id: 0,
                author: insertionAuthor,
                date: insertionDate,
              })
            );
          } else if (deletionMark) {
            runs.push(
              new DeletedTextRun({
                ...baseOptions,
                id: 1,
                author: deletionAuthor,
                date: deletionDate,
              })
            );
          } else {
            runs.push(buildTextOrLinkRun());
          }

          // Emit CommentRangeEnd + CommentReference for each comment that ends here
          for (const cId of activeCommentIds) {
            const seenSoFar = commentSeenOccurrences.get(cId) || 1;
            const totalCount = commentTotalOccurrences.get(cId) || 1;
            if (seenSoFar === totalCount) {
              const numericId = commentIdMap.get(cId)!;
              runs.push(new CommentRangeEnd(numericId));
              runs.push(
                new TextRun({
                  children: [new CommentReference(numericId)] as any,
                })
              );
            }
          }
        } else if (insertionMark) {
          runs.push(
            new InsertedTextRun({
              ...baseOptions,
              id: 0,
              author: insertionAuthor,
              date: insertionDate,
            })
          );
        } else if (deletionMark) {
          runs.push(
            new DeletedTextRun({
              ...baseOptions,
              id: 1,
              author: deletionAuthor,
              date: deletionDate,
            })
          );
        } else {
          runs.push(buildTextOrLinkRun());
        }
      } else if (node.type === "footnoteRef") {
        const id = node.attrs?.footnoteId as string | undefined;
        const isEndnote = node.attrs?.kind === "endnote";
        if (id && !isEndnote && footnoteNumbers.has(id)) {
          runs.push(new FootnoteReferenceRun(footnoteNumbers.get(id)!));
        } else if (id && isEndnote && endnoteNumbers.has(id)) {
          runs.push(
            new TextRun({
              text: toRoman(endnoteNumbers.get(id)!),
              superScript: true,
            })
          );
        }
      } else if (node.type === "hardBreak") {
        runs.push(new TextRun({ break: 1 }));
      } else if (node.type === "inlineMath") {
        const latex = node.attrs?.latex || "";
        const math = latexToDocxMath(latex);
        if (math) {
          runs.push(math);
        } else {
          runs.push(
            new TextRun({
              text: `$${latex}$`,
              italics: true,
            })
          );
        }
      } else if (node.type === "image") {
        const src = node.attrs?.src || "";
        const alt = node.attrs?.alt || "";

        if (src && src.startsWith("data:image/")) {
          try {
            const matches = src.match(/^data:image\/(\w+);base64,(.+)$/);
            if (matches) {
              const base64Data = matches[2];
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }

              runs.push(
                new ImageRun({
                  data: bytes,
                  transformation: {
                    width: 200,
                    height: 150,
                  },
                })
              );
              continue;
            }
          } catch (error) {
            console.error("Error processing inline image:", error);
          }
        }

        const prefetchedInline = imageBytesBySrc.get(src);
        if (prefetchedInline) {
          const w = Number(node.attrs?.width);
          const h = Number(node.attrs?.height);
          runs.push(
            new ImageRun({
              data: prefetchedInline,
              transformation: {
                width: Number.isFinite(w) && w > 0 ? w : 200,
                height: Number.isFinite(h) && h > 0 ? h : 150,
              },
            })
          );
          continue;
        }

        runs.push(
          new TextRun({
            text: `[Image: ${alt || src || "image"}]`,
            italics: true,
          })
        );
      }
    }

    return runs;
  }

  function getTextContent(node: TipTapNode): string {
    if (node.type === "text") {
      return node.text || "";
    }
    if (node.content) {
      return node.content.map(getTextContent).join("");
    }
    return "";
  }

  // Collect footnote bodies for Document({ footnotes }) — must mirror the
  // numbering assigned by prescanFootnotes. Word renders these at the bottom
  // of the page containing each reference.
  const docxFootnotes: Record<number, { children: Paragraph[] }> = {};
  function collectFootnoteItems(node: TipTapNode) {
    if (node.type === "footnotesSection" && node.attrs?.kind !== "endnote") {
      for (const item of node.content || []) {
        if (item.type !== "footnoteItem") continue;
        const num = footnoteNumbers.get(item.attrs?.footnoteId || "");
        if (!num) continue;
        const paragraphs = (item.content || [])
          .flatMap(child => processNode(child))
          .filter((c): c is Paragraph => c instanceof Paragraph);
        docxFootnotes[num] = {
          children: paragraphs.length > 0 ? paragraphs : [new Paragraph({})],
        };
      }
    }
    if (node.content) {
      for (const child of node.content) {
        collectFootnoteItems(child);
      }
    }
  }
  collectFootnoteItems(json);
  // Every numbered ref needs a body, even when the note is still empty
  footnoteNumbers.forEach(num => {
    if (!docxFootnotes[num]) {
      docxFootnotes[num] = { children: [new Paragraph({})] };
    }
  });

  // Process the document
  const docChildren = processNode(json);

  // Create the Word document
  // Build comments array for Word document (including replies)
  const wordComments = commentsData
    .filter(comment => seenCommentIds.has(comment.id))
    .map(comment => {
      // Build paragraphs for the comment - main content plus replies
      const commentParagraphs: Paragraph[] = [
        new Paragraph({
          children: [new TextRun(comment.content)],
        }),
      ];

      // Add replies as additional paragraphs with author attribution
      if (comment.replies && comment.replies.length > 0) {
        for (const reply of comment.replies) {
          commentParagraphs.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${reply.author}: `, bold: true }),
                new TextRun(reply.content),
              ],
            })
          );
        }
      }

      return {
        id: commentIdMap.get(comment.id)!,
        author: comment.author,
        date: comment.createdAt,
        children: commentParagraphs,
      };
    });

  // Build header/footer objects for the DOCX section
  const hf = options.headerFooter;

  /**
   * Convert one zone's HTML into TextRuns ({page} → live PAGE field,
   * {pages} → live NUMPAGES field, tags stripped to plain text).
   */
  function htmlZoneToRuns(html: string | undefined): TextRun[] {
    if (!html || !html.trim()) return [];
    const runs: TextRun[] = [];
    const parts = html.split(/(\{page\}|\{pages\})/g);
    for (const part of parts) {
      if (part === "{page}") {
        runs.push(new TextRun({ children: [PageNumber.CURRENT] }));
      } else if (part === "{pages}") {
        runs.push(new TextRun({ children: [PageNumber.TOTAL_PAGES] }));
      } else {
        const text = decodeHtmlEntities(part.replace(/<[^>]*>/g, ""));
        if (text.trim()) {
          runs.push(new TextRun({ text }));
        }
      }
    }
    return runs;
  }

  // Tab stops sized to the page: CENTER at half the usable width, RIGHT at
  // the right margin — so zone headers land correctly on A4/Legal too.
  const exportPageSetup = options.pageSetup ?? DEFAULT_PAGE_SETUP;
  const pageDims = pageDimensionsIn(exportPageSetup);
  const usableWidthIn = Math.max(
    1,
    pageDims.widthIn -
      exportPageSetup.margins.left -
      exportPageSetup.margins.right
  );

  /**
   * Build the Word-conventional single header/footer paragraph: left text,
   * TAB, centered text, TAB, right text — with CENTER/RIGHT tab stops at
   * half / full usable page width.
   */
  function buildZoneParagraph(
    left?: string,
    center?: string,
    right?: string
  ): Paragraph | null {
    const leftRuns = htmlZoneToRuns(left);
    const centerRuns = htmlZoneToRuns(center);
    const rightRuns = htmlZoneToRuns(right);
    if (!leftRuns.length && !centerRuns.length && !rightRuns.length) {
      return null;
    }
    // Always write BOTH tabs when anything follows the left zone, even if a
    // zone is empty ("\tCENTER\t") — re-import splits on tabs, and two tabs
    // unambiguously mean left/center/right (one tab would read as left/right).
    const children: TextRun[] = [...leftRuns];
    if (centerRuns.length || rightRuns.length) {
      children.push(new TextRun({ children: [new Tab()] }));
      children.push(...centerRuns);
      children.push(new TextRun({ children: [new Tab()] }));
      children.push(...rightRuns);
    }
    return new Paragraph({
      children,
      tabStops: [
        {
          type: TabStopType.CENTER,
          position: convertInchesToTwip(usableWidthIn / 2),
        },
        {
          type: TabStopType.RIGHT,
          position: convertInchesToTwip(usableWidthIn),
        },
      ],
    });
  }

  function buildDocxHeader(
    left?: string,
    right?: string,
    center?: string
  ): Header | undefined {
    const paragraph = buildZoneParagraph(left, center, right);
    if (!paragraph) return undefined;
    return new Header({ children: [paragraph] });
  }

  function buildDocxFooter(
    left?: string,
    right?: string,
    center?: string
  ): Footer | undefined {
    const paragraph = buildZoneParagraph(left, center, right);
    if (!paragraph) return undefined;
    return new Footer({ children: [paragraph] });
  }

  const sectionHeaders: Record<string, Header> = {};
  const sectionFooters: Record<string, Footer> = {};

  if (hf) {
    const defaultHeader = buildDocxHeader(
      hf.headerLeft,
      hf.headerRight,
      hf.headerCenter
    );
    const defaultFooter = buildDocxFooter(
      hf.footerLeft,
      hf.footerRight,
      hf.footerCenter
    );
    if (defaultHeader) sectionHeaders.default = defaultHeader;
    if (defaultFooter) sectionFooters.default = defaultFooter;

    if (hf.differentFirstPage) {
      const firstHeader = buildDocxHeader(
        hf.firstPageHeaderLeft,
        hf.firstPageHeaderRight,
        hf.firstPageHeaderCenter
      );
      const firstFooter = buildDocxFooter(
        hf.firstPageFooterLeft,
        hf.firstPageFooterRight,
        hf.firstPageFooterCenter
      );
      if (firstHeader) sectionHeaders.first = firstHeader;
      if (firstFooter) sectionFooters.first = firstFooter;
    }
  }

  const doc = new Document({
    creator: author,
    title: options.title || "Document",
    revision: 1,
    comments: wordComments.length > 0 ? { children: wordComments } : undefined,
    footnotes:
      Object.keys(docxFootnotes).length > 0 ? docxFootnotes : undefined,
    sections: [
      {
        properties: {
          page: pageSetupToDocxPage(exportPageSetup),
          ...(hf?.differentFirstPage ? { titlePage: true } : {}),
        },
        headers:
          Object.keys(sectionHeaders).length > 0 ? sectionHeaders : undefined,
        footers:
          Object.keys(sectionFooters).length > 0 ? sectionFooters : undefined,
        children: docChildren.length > 0 ? docChildren : [new Paragraph({})],
      },
    ],
    numbering: {
      config: [
        // Simple numbered list (1., 2., 3.)
        {
          reference: "default-numbering",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
          ],
        },
        // Multi-level outline numbering (1., 1.1., 1.1.1., etc.)
        {
          reference: "outline-numbering",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.25),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.35),
                  },
                },
              },
            },
            {
              level: 2,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.%3.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.75),
                    hanging: convertInchesToTwip(0.5),
                  },
                },
              },
            },
            {
              level: 3,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.%3.%4.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(1.0),
                    hanging: convertInchesToTwip(0.6),
                  },
                },
              },
            },
            {
              level: 4,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.%3.%4.%5.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(1.25),
                    hanging: convertInchesToTwip(0.7),
                  },
                },
              },
            },
            {
              level: 5,
              format: LevelFormat.DECIMAL,
              text: "%1.%2.%3.%4.%5.%6.",
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(1.5),
                    hanging: convertInchesToTwip(0.8),
                  },
                },
              },
            },
          ],
        },
      ],
    },
  });

  // Generate the DOCX blob
  try {
    const blob = await Packer.toBlob(doc);
    return blob;
  } catch (err) {
    console.error("[docxExport] Packer.toBlob failed:", err);
    throw new Error(
      "Failed to generate DOCX file. The document may contain unsupported content."
    );
  }
}

/**
 * Helper function to download the DOCX blob
 */
export function downloadDocx(
  blob: Blob,
  filename: string = "document.docx"
): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
